import { Button, Container, Paper, Text, Stack } from "@mantine/core";
import { useContext, useEffect } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import React from "react";
import {
  Checkbox,
  Select,
  TextInput,
  Textarea,
  Radio,
} from "react-hook-form-mantine";
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import {
  useGetPoll,
  useUpdatePoll,
  useGetAvailableFollowUpPolls,
} from "../../api/polls";
import { AuthContext } from "../../services/context";
import { getCurrentWorkspace } from "../../services/userConfig";
import { omit } from "lodash";
import { web_updatePollsSchema } from "@congrs/schemas";
import resolver from "@congrs/resolver";
import { modals } from "@mantine/modals";

export default function EditPoll() {
  const { user } = useContext(AuthContext);
  const currentWorkspace = getCurrentWorkspace(user);

  const { id } = useParams();
  const { data, isPending, isError, error } = useGetPoll(id);

  const { mutate } = useUpdatePoll({
    id,
    ...data,
  });

  const { data: availableFollowUpPolls } = useGetAvailableFollowUpPolls({
    eventId: currentWorkspace.eventId,
    sessionId: data?.sessionId,
    excludePollId: id,
  });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isDirty },
  } = useForm({
    defaultValues: {
      sessionId: String(data?.sessionId),
      title: data?.title,
      description: data?.description,
      visibility: data?.isVisible ? "visible" : "hidden",
      status: data?.status,
      results: data?.results ? "shared" : "hidden",
      options: data?.options,
      type: data?.type,
      followUpConditionValueForChoice:
        data?.followUpConditionValueForChoice || [],
      followUpConditionValueForText: data?.followUpConditionValueForText || "",
      followUpConditionType: data?.followUpConditionType || "isEqual",
      followUpPollId: data?.followUpPollId,
      followUpQuestion: !!data?.followUpPollId,
      workspaceId: currentWorkspace.id,
      eventId: currentWorkspace.eventId,
    },
    resolver: resolver(web_updatePollsSchema),
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "options",
  });

  useEffect(() => {
    if (data) {
      reset({
        sessionId: String(data?.sessionId),
        title: data?.title,
        description: data?.description,
        visibility: data?.isVisible ? "visible" : "hidden",
        status: data?.status,
        results: data?.areResultsVisible ? "shared" : "hidden",
        options: data?.options,
        type: data?.type,
        followUpConditionValueForChoice:
          data.type === "singleChoice" || data.type === "multipleChoice"
            ? Array.isArray(data?.followUpConditionValue)
              ? data.followUpConditionValue
              : []
            : [],
        followUpConditionValueForText:
          data.type === "text" && data?.followUpConditionValue?.[0]?.text
            ? data.followUpConditionValue[0].text
            : "",
        followUpConditionType: data?.followUpConditionType,
        followUpPollId: data?.followUpPollId
          ? String(data?.followUpPollId)
          : null,
        followUpQuestion: !!data?.followUpPollId,
        workspaceId: currentWorkspace.id,
        eventId: currentWorkspace.eventId,
      });
    }
  }, [data, reset]);

  const pollType = watch("type");
  const followUpQuestion = watch("followUpQuestion");
  const followUpPollId = watch("followUpPollId");
  const followUpConditionValueForChoice =
    watch("followUpConditionValueForChoice") ?? [];
  const followUpConditionValueForText = watch("followUpConditionValueForText");
  const followUpConditionType = watch("followUpConditionType");
  const options = watch("options");

  useEffect(() => {
    if (followUpQuestion && followUpConditionType == null) {
      setValue("followUpConditionType", "isEqual");
    }
  }, [followUpQuestion, setValue]);

  const [prevType, setPrevType] = React.useState(null);

  useEffect(() => {
    if (pollType === prevType || pollType == null) return;

    if (
      data?.followUpPollId &&
      prevType &&
      prevType !== "text" &&
      pollType === "text"
    ) {
      openTypeChangeModal({
        prevType,
        setValue,
        remove,
        onConfirm: () => setPrevType("text"),
        onCancel: () => {
          setValue("type", prevType);
          setPrevType(prevType);
        },
      });
    } else {
      setPrevType(pollType);

      if (pollType === "text") {
        setValue("options", []);
        setValue("followUpQuestion", false);
        setValue("followUpConditionType", null);
        setValue("followUpConditionValueForText", "");
        setValue("followUpConditionValueForChoice", []);
        setValue("followUpPollId", null);
      } else if (!options || options.length === 0) {
        append({ id: uuidv4(), title: "" });
        append({ id: uuidv4(), title: "" });
      }
    }
  }, [pollType]);

  const onSubmit = (data) => {
    if (data.type === "text" && data.followUpQuestion) {
      data.followUpQuestion = false;
    }

    const followUpConditionValue = data.followUpQuestion
      ? data.type === "text"
        ? [{ text: followUpConditionValueForText }]
        : followUpConditionValueForChoice
      : null;

    const submissionData = {
      ...data,
      options: data.type === "text" ? null : data.options,
      followUpConditionValue,
      followUpConditionType: data.followUpQuestion
        ? data.followUpConditionType
        : null,
      followUpPollId: data.followUpQuestion ? followUpPollId : null,
      isVisible: data.visibility === "visible",
      areResultsVisible: data.results === "shared",
    };

    mutate(omit(submissionData, ["results", "visibility"]));
  };

  if (isPending) return <Text>Loading...</Text>;

  if (isError) return <Text c="red">{error.message}</Text>;

  const handleCheckboxChange = (e, value) => {
    const isChecked = e.target.checked;

    let updatedFollowUpConditionValue;

    if (pollType === "singleChoice") {
      updatedFollowUpConditionValue = isChecked ? [{ id: value }] : [];
    } else {
      updatedFollowUpConditionValue = isChecked
        ? [...followUpConditionValueForChoice, { id: value }]
        : followUpConditionValueForChoice.filter((item) => item.id !== value);
    }

    setValue("followUpConditionValueForChoice", updatedFollowUpConditionValue);
  };

  return (
    <Container size={500}>
      <Paper withBorder shadow="md" p={16} mt={16} radius="md">
        <form onSubmit={handleSubmit(onSubmit)}>
          <TextInput
            label="Question"
            placeholder="Edit question"
            type="text"
            name="title"
            withAsterisk
            defaultValue={data?.title}
            mt="md"
            control={control}
          />
          <Textarea
            label="Description"
            placeholder="Edit description"
            type="text"
            name="description"
            defaultValue={data?.description}
            control={control}
            mt="md"
          />
          <div style={{ marginTop: 20 }}>
            <label style={styles.label}>Question Type</label>
            <Radio.Group control={control} name="type">
              <Stack>
                <Radio
                  control={control}
                  value="singleChoice"
                  name="type"
                  label="Single choice question"
                />
                <Radio
                  control={control}
                  value="multipleChoice"
                  name="type"
                  label="Multiple choice question"
                />
                <Radio
                  control={control}
                  value="text"
                  name="type"
                  label="Text question"
                />
              </Stack>
            </Radio.Group>
          </div>
          {watch("type") !== "text" && (
            <div style={{ marginTop: 20 }}>
              <label style={styles.label}>Options</label>
              {fields?.map((field, index) => (
                <div
                  key={field.id}
                  style={{ display: "flex", marginBottom: 20 }}
                >
                  <TextInput
                    name={`options.${index}.id`}
                    value={field.id}
                    control={control}
                    type="hidden"
                    defaultValue={field.id}
                  />
                  <TextInput
                    placeholder="Option"
                    type="text"
                    name={`options.${index}.title`}
                    control={control}
                    defaultValue={field.title}
                  />
                  <Button
                    color="gray"
                    style={{ marginLeft: 10, marginRight: 10 }}
                    type="button"
                    onClick={() => append({ id: uuidv4(), title: "" })}
                  >
                    +
                  </Button>
                  <Button
                    color="gray"
                    type="button"
                    onClick={() => remove(index)}
                    disabled={fields.length === 2}
                  >
                    -
                  </Button>
                </div>
              ))}
            </div>
          )}
          <div style={styles.wrapper}>
            <Select
              control={control}
              label="Visibility"
              name="visibility"
              data={[
                { value: "visible", label: "Visible" },
                { value: "hidden", label: "Hidden" },
              ]}
              allowDeselect={false}
            />
            <Select
              control={control}
              label="Status"
              name="status"
              data={[
                {
                  value: "open",
                  label: "Open",
                },
                {
                  value: "closed",
                  label: "Closed",
                },
              ]}
              allowDeselect={false}
            />
            <Select
              control={control}
              label="Results"
              name="results"
              data={[
                { value: "shared", label: "Shared" },
                { value: "hidden", label: "Hidden" },
              ]}
              description="Are the results of this poll shared with the users after they submit their vote?"
              allowDeselect={false}
            />
          </div>
          {watch("type") !== "text" && !data?.isFollowUp && (
            <div style={{ marginTop: 20 }}>
              <Checkbox
                label="Follow Up Question"
                name={`followUpQuestion`}
                mt="md"
                control={control}
              />
              {watch("followUpQuestion") && (
                <>
                  <div style={{ marginTop: 20 }}>
                    <label style={styles.label}>
                      Follow Up Question Condition
                    </label>
                  </div>
                  <Radio.Group control={control} name={`followUpConditionType`}>
                    <Stack>
                      <Radio
                        control={control}
                        value="isEqual"
                        name={`followUpConditionType`}
                        label="is Equal"
                      />
                      <Radio
                        control={control}
                        value="isNotEqual"
                        name={`followUpConditionType`}
                        label="is Not Equal"
                      />
                    </Stack>
                  </Radio.Group>
                  {pollType !== "text" && (
                    <Stack>
                      <div style={{ marginTop: 10, marginBottom: -10 }}>
                        <label style={styles.label}>
                          Follow Up Question Conditions Value(s)
                        </label>
                      </div>
                      {options?.map((option) => (
                        <Checkbox
                          key={option.id}
                          label={option.title}
                          control={control}
                          name="followUpConditionValueForChoice"
                          checked={followUpConditionValueForChoice
                            ?.map((i) => i.id)
                            ?.includes(option.id)}
                          onChange={(e) => handleCheckboxChange(e, option.id)}
                          value={option.id}
                        />
                      ))}
                    </Stack>
                  )}
                  <Select
                    control={control}
                    label="Follow Up Poll"
                    name="followUpPollId"
                    placeholder="Select follow up poll"
                    data={
                      availableFollowUpPolls?.map((poll) => ({
                        value: String(poll.id),
                        label: poll.title,
                      })) || []
                    }
                    allowDeselect
                    searchable
                    nothingFoundMessage="Nothing found..."
                    mt="md"
                  />
                </>
              )}
            </div>
          )}
          <Button type="submit" mt="md" disabled={!isDirty}>
            Update Poll
          </Button>
        </form>
      </Paper>
    </Container>
  );
}

const openTypeChangeModal = ({
  setValue,
  prevType,
  remove,
  onConfirm,
  onCancel,
}) => {
  modals.openConfirmModal({
    modalId: "confirmModal",
    title: "Are you sure?",
    children: (
      <Text>
        You are about to change the type of question to text. The follow up to
        this question will no longer be connected and it will become a regular
        question.
      </Text>
    ),
    labels: { confirm: "Confirm", cancel: "Cancel" },
    onCancel: () => {
      setValue("type", prevType);
      onCancel();
    },
    onConfirm: () => {
      remove();
      setValue("followUpPollId", null);
      setValue("followUpQuestion", false);
      onConfirm();
    },
  });
};

const styles = {
  label: {
    display: "flex",
    fontSize: 14,
    fontWeight: 500,
    marginBottom: 10,
  },
  wrapper: {
    marginTop: 20,
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
};
